// Motion.dev Site Navigation Animations
// Smooth spring-based animations for mobile menu

// Import Motion.dev from CDN
// Note: This will be loaded via script tag in the HTML head

class SiteNavMotion {
  constructor() {
    this.toggle = document.querySelector('.site-header__toggle');
    this.header = document.querySelector('.site-header');
    this.navWrapper = document.querySelector('.site-header__nav-wrapper');
    this.navLinks = document.querySelectorAll('.site-header__nav-link');
    this.navSecondaryLinks = document.querySelectorAll('.site-header__nav-secondary-link');
    this.navSocialLinks = document.querySelectorAll('.site-header__nav-social-link');
    this.navCloseButton = document.querySelector('.site-header__nav-close');
    
    this.isAnimating = false;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    
    this.init();
  }

  init() {
    if (!this.toggle || !this.header || !this.navWrapper) return;
    
    this.bindEvents();
    this.initializeMobileMenuState();
    this.setupScrollBehavior();
  }

  updateFocusableElements() {
    this.focusableElements = this.navWrapper.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  async openMobileMenu() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Set initial states
    document.body.classList.add('menu-open');
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Check if Motion is available, fallback to CSS if not
    if (typeof Motion !== 'undefined' && Motion.animate) {
      const { animate, stagger } = Motion;
      
      // Explicitly set the starting position using transform string
      // This ensures Motion.dev knows exactly where to start from
      this.navWrapper.style.opacity = '1';
      this.navWrapper.style.pointerEvents = 'auto';
      
      // Force the starting position explicitly using direct style setting
      this.navWrapper.style.transform = 'translateX(-100%)';
      
      // Now animate to final position using Motion.dev x property
      animate(this.navWrapper, 
        { x: 0 }, 
        { type: "spring", bounce: 0.15, duration: 0.5 }
      );

      // Stagger all menu items as one continuous sequence
      setTimeout(() => {
        // Create one big array of all elements in order
        const allMenuElements = [
          ...Array.from(this.navLinks),
          ...Array.from(this.navSecondaryLinks), 
          ...Array.from(this.navSocialLinks)
        ];
        
        // Animate all elements with a single stagger
        animate(allMenuElements, 
          { opacity: 1, x: 0, scale: 1 }, 
          { delay: stagger(0.05), type: "spring", bounce: 0.1, duration: 0.4 }
        );
      }, 150);

    } else {
      // Fallback: Use CSS classes for animation
      this.navWrapper.style.opacity = '1';
      this.navWrapper.style.transform = 'translateX(0)';
      this.navWrapper.style.pointerEvents = 'auto';
      
      // Stagger effect with setTimeout
      this.navLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '1';
          link.style.transform = 'translateX(0)';
        }, index * 80);
      });
      
      this.navSecondaryLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '1';
          link.style.transform = 'translateX(0)';
        }, 250 + index * 60);
      });
      
      this.navSocialLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '1';
          link.style.transform = 'scale(1)';
        }, 350 + index * 40);
      });
    }
    
    // Focus management
    this.updateFocusableElements();
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
    
    this.isAnimating = false;
  }

  async closeMobileMenu() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Check if Motion is available, fallback to CSS if not
    if (typeof Motion !== 'undefined' && Motion.animate) {
      const { animate, stagger } = Motion;
      
      // Create unified exit animation for all menu elements
      const allMenuElements = [
        ...Array.from(this.navLinks),
        ...Array.from(this.navSecondaryLinks), 
        ...Array.from(this.navSocialLinks)
      ];
      
      // Stagger out all elements as one sequence (reverse order)
      animate(allMenuElements, 
        { opacity: 0, x: -20, scale: 0.9 }, 
        { delay: stagger(0.03, { from: 'last' }), type: "spring", bounce: 0.05, duration: 0.25 }
      );

      // Then slide out the wrapper smoothly  
      setTimeout(() => {
        animate(this.navWrapper, 
          { x: '-100%' }, 
          { type: "spring", bounce: 0.05, duration: 0.35 }
        ).finished.then(() => {
          // Reset CSS properties after animation completes
          this.navWrapper.style.opacity = '0';
          this.navWrapper.style.pointerEvents = 'none';
          
          // Clean up states after animation (this removes overlay too)
          document.body.classList.remove('menu-open');
          this.toggle.classList.remove('is-active');
          this.toggle.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = '';
          
          // Return focus to toggle button
          this.toggle.focus();
          
          this.isAnimating = false;
        });
      }, 200);
      
      // Don't wait here - let the animation promise handle cleanup
      return;
      
    } else {
      // Fallback: Use CSS for animation
      const reverseNavSocialLinks = Array.from(this.navSocialLinks).reverse();
      const reverseNavSecondaryLinks = Array.from(this.navSecondaryLinks).reverse();
      const reverseNavLinks = Array.from(this.navLinks).reverse();
      
      // Stagger out social links
      reverseNavSocialLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '0';
          link.style.transform = 'scale(0.8)';
        }, index * 30);
      });
      
      // Stagger out secondary links
      reverseNavSecondaryLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '0';
          link.style.transform = 'translateX(-20px)';
        }, 100 + index * 40);
      });
      
      // Stagger out main links
      reverseNavLinks.forEach((link, index) => {
        setTimeout(() => {
          link.style.opacity = '0';
          link.style.transform = 'translateX(-30px)';
        }, 150 + index * 50);
      });
      
      // Slide out nav wrapper
      setTimeout(() => {
        this.navWrapper.style.opacity = '0';
        this.navWrapper.style.transform = 'translateX(-100%)';
        this.navWrapper.style.pointerEvents = 'none';
      }, 250);
      
      // Wait for animation to complete
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Clean up states for fallback
      document.body.classList.remove('menu-open');
      this.toggle.classList.remove('is-active');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      
      // Return focus to toggle button
      this.toggle.focus();
      
      this.isAnimating = false;
    }
  }

  toggleMenu() {
    const isOpen = document.body.classList.contains('menu-open');
    if (isOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  bindEvents() {
    // Toggle menu on button click
    this.toggle.addEventListener('click', () => this.toggleMenu());

    // Close button
    if (this.navCloseButton) {
      this.navCloseButton.addEventListener('click', () => {
        if (document.body.classList.contains('menu-open')) {
          this.closeMobileMenu();
        }
      });
    }

    // Close menu when clicking nav links
    this.navLinks.forEach(link => {
      link.addEventListener('click', () => {
        if (document.body.classList.contains('menu-open')) {
          this.closeMobileMenu();
        }
      });
    });

    // Keyboard navigation
    this.navWrapper.addEventListener('keydown', (event) => {
      if (!document.body.classList.contains('menu-open')) return;

      // Close on Escape key
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMobileMenu();
        return;
      }

      // Tab navigation within menu
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === this.firstFocusableElement) {
            event.preventDefault();
            this.lastFocusableElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === this.lastFocusableElement) {
            event.preventDefault();
            this.firstFocusableElement.focus();
          }
        }
      }
    });

    // Close menu when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.header.contains(event.target) && 
          !this.navWrapper.contains(event.target) && 
          document.body.classList.contains('menu-open')) {
        this.closeMobileMenu();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768 && document.body.classList.contains('menu-open')) {
        this.closeMobileMenu();
      }
      // Reinitialize menu state on resize
      this.initializeMobileMenuState();
    });
  }

  initializeMobileMenuState() {
    // Small delay to ensure DOM is fully settled
    setTimeout(() => {
      // Always ensure menu starts hidden, regardless of screen size
      document.body.classList.remove('menu-open');
      this.toggle.classList.remove('is-active');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      
      // Reset all elements to initial positions using direct style setting
      this.navWrapper.style.opacity = '0';
      this.navWrapper.style.transform = 'translateX(-100%)';
      this.navWrapper.style.pointerEvents = 'none';
      
      // Reset menu items to initial states
      this.navLinks.forEach(link => {
        link.style.opacity = '0';
        link.style.transform = 'translateX(-30px)';
      });
      
      this.navSecondaryLinks.forEach(link => {
        link.style.opacity = '0';
        link.style.transform = 'translateX(-20px)';
      });
      
      this.navSocialLinks.forEach(link => {
        link.style.opacity = '0';
        link.style.transform = 'scale(0.8)';
      });
    }, 10);
  }

  setupScrollBehavior() {
    // Header hide on scroll functionality (if enabled)
    const hideOnScroll = this.header.dataset.hideOnScroll === 'true';
    
    if (hideOnScroll) {
      let lastScrollY = 0;
      let ticking = false;
      const scrollThreshold = 10;

      const updateHeaderOnScroll = () => {
        const currentScrollY = window.scrollY;
        const headerHeight = this.header.offsetHeight;
        const scrollingDown = currentScrollY > lastScrollY + scrollThreshold;
        const scrollingUp = currentScrollY < lastScrollY - scrollThreshold;
        const scrolledPastHeader = currentScrollY > headerHeight;
        const isAtTop = currentScrollY <= 0;
        const isMenuOpen = document.body.classList.contains('menu-open');

        // Always show shadow when scrolled past top
        this.header.classList.toggle('header-scrolled', currentScrollY > 0);

        // Don't hide header if mobile menu is open or at top of page
        if (isMenuOpen || isAtTop) {
          this.header.classList.remove('header-hidden');
        } else if (scrollingDown && scrolledPastHeader) {
          this.header.classList.add('header-hidden');
        } else if (scrollingUp) {
          this.header.classList.remove('header-hidden');
        }

        lastScrollY = currentScrollY;
        ticking = false;
      };

      const requestHeaderUpdate = () => {
        if (!ticking) {
          requestAnimationFrame(updateHeaderOnScroll);
          ticking = true;
        }
      };

      // Throttled scroll listener
      window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
    }
  }
}

// Initialize when DOM is ready and Motion library is available
function initializeSiteNav() {
  // Check if Motion is available, if not wait a bit longer
  if (typeof Motion === 'undefined' || !Motion.animate) {
    console.log('Motion.dev hybrid not yet loaded, waiting...');
    setTimeout(initializeSiteNav, 100);
    return;
  }
  
  console.log('Motion.dev hybrid loaded successfully!');
  new SiteNavMotion();
}

document.addEventListener('DOMContentLoaded', initializeSiteNav);

// Handle reduced motion preference
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

if (prefersReducedMotion.matches) {
  // Override Motion.dev with simpler animations for reduced motion
  console.log('Reduced motion preference detected - using simplified animations');
}
